import { IFDEntry } from "./ifd"
import { DataStream } from "./data-stream"

export enum JpegMarkers {
	SOF0 = 0xffc0, //	Start of Frame 0	Baseline DCT
	SOF1 = 0xffc1, //	Start of Frame 1	Extended Sequential DCT
	SOF2 = 0xffc2, //	Start of Frame 2	Progressive DCT
	SOF3 = 0xffc3, //	Start of Frame 3	Lossless (sequential)
	DHT = 0xffc4, //	Define Huffman Table
	SOF5 = 0xffc5, //	Start of Frame 5	Differential sequential DCT
	SOF6 = 0xffc6, //	Start of Frame 6	Differential progressive DCT
	SOF7 = 0xffc7, //	Start of Frame 7	Differential lossless (sequential)
	JPG = 0xffc8, //	JPEG Extensions
	SOF9 = 0xffc9, //	Start of Frame 9	Extended sequential DCT, Arithmetic coding
	SOF10 = 0xffca, //	Start of Frame 10	Progressive DCT, Arithmetic coding
	SOF11 = 0xffcb, //	Start of Frame 11	Lossless (sequential), Arithmetic coding
	DAC = 0xffcc, //	Define Arithmetic Coding
	SOF13 = 0xffcd, //	Start of Frame 13	Differential sequential DCT, Arithmetic coding
	SOF14 = 0xffce, //	Start of Frame 14	Differential progressive DCT, Arithmetic coding
	SOF15 = 0xffcf, //	Start of Frame 15	Differential lossless (sequential), Arithmetic coding
	RST0 = 0xffd0, //	Restart Marker 0
	RST1 = 0xffd1, //	Restart Marker 1
	RST2 = 0xffd2, //	Restart Marker 2
	RST3 = 0xffd3, //	Restart Marker 3
	RST4 = 0xffd4, //	Restart Marker 4
	RST5 = 0xffd5, //	Restart Marker 5
	RST6 = 0xffd6, //	Restart Marker 6
	RST7 = 0xffd7, //	Restart Marker 7
	SOI = 0xffd8, //	Start of Image
	EOI = 0xffd9, //	End of Image
	SOS = 0xffda, //	Start of Scan
	DQT = 0xffdb, //	Define Quantization Table
	DNL = 0xffdc, //	Define Number of Lines	(Not common)
	DRI = 0xffdd, //	Define Restart Interval
	DHP = 0xffde, //	Define Hierarchical Progression	(Not common)
	EXP = 0xffdf, //	Expand Reference Component	(Not common)
	APP0 = 0xffe0, //	Application Segment 0	JFIF – JFIF JPEG image AVI1 – Motion JPEG (MJPG)
	APP1 = 0xffe1, //	Application Segment 1	EXIF Metadata, TIFF IFD format, JPEG Thumbnail (160×120) Adobe XMP
	APP2 = 0xffe2, //	Application Segment 2	ICC color profile, FlashPix
	APP3 = 0xffe3, //	Application Segment 3	(Not common) JPS Tag for Stereoscopic JPEG images
	APP4 = 0xffe4, //	Application Segment 4	(Not common)
	APP5 = 0xffe5, //	Application Segment 5	(Not common)
	APP6 = 0xffe6, //	Application Segment 6	(Not common) NITF Lossles profile
	APP7 = 0xffe7, //	Application Segment 7	(Not common)
	APP8 = 0xffe8, //	Application Segment 8	(Not common)
	APP9 = 0xffe9, //	Application Segment 9	(Not common)
	APP10 = 0xffea, //	Application Segment 10 PhoTags	(Not common) ActiveObject (multimedia messages / captions)
	APP11 = 0xffeb, //	Application Segment 11	(Not common) HELIOS JPEG Resources (OPI Postscript)
	APP12 = 0xffec, //	Application Segment 12	Picture Info (older digicams), Photoshop Save for Web: Ducky
	APP13 = 0xffed, //	Application Segment 13	Photoshop Save As: IRB, 8BIM, IPTC
	APP14 = 0xffee, //	Application Segment 14	(Not common)
	APP15 = 0xffef, //	Application Segment 15	(Not common)
	JPG0 = 0xfff0, //	JPEG Extension 0
	JPG1 = 0xfff1, //	JPEG Extension 1
	JPG2 = 0xfff2, //	JPEG Extension 2
	JPG3 = 0xfff3, //	JPEG Extension 3
	JPG4 = 0xfff4, //	JPEG Extension 4
	JPG5 = 0xfff5, //	JPEG Extension 5
	JPG6 = 0xfff6, //	JPEG Extension 6
	JPG7 = 0xfff7, // SOF48	JPEG Extension 7 JPEG-LS	Lossless JPEG
	JPG8 = 0xfff8, // LSE	JPEG Extension 8 JPEG-LS Extension	Lossless JPEG Extension Parameters
	JPG9 = 0xfff9, //	JPEG Extension 9	(Not common)
	JPG10 = 0xfffa, //	JPEG Extension 10	(Not common)
	JPG11 = 0xfffb, //	JPEG Extension 11	(Not common)
	JPG12 = 0xfffc, //	JPEG Extension 12	(Not common)
	JPG13 = 0xfffd, //	JPEG Extension 13	(Not common)
	COM = 0xfffe, //	Comment
}

interface MarkerInfo {
	id: string
	start: number
	end: number
}

type MarkerName = keyof typeof JpegMarkers
type MarkerMap = Partial<Record<MarkerName, MarkerInfo>>

export interface Metadata {
	TIFF: IFDEntry[]
	Exif: IFDEntry[]
	XMP: { [key: string]: any }
	ICCProfile?: Uint8Array
}

export function getJpegMarkersFromBytes(bytes: Uint8Array) {
	const markers: MarkerMap = {}

	// Grab first KB off the top
	const header = bytes.slice(0, 1024).buffer
	const dataStream = new DataStream(header)

	// JPEG data is always big-endian
	dataStream.littleEndian = false

	// Check file type
	const magicNumber = dataStream.getUint16()

	if (magicNumber !== JpegMarkers.SOI) {
		throw new Error("Not a valid JPEG")
	}

	while (dataStream.offset < header.byteLength - 8) {
		const sectionStartOffset = dataStream.offset
		const sectionMarker = dataStream.getUint16()

		if (sectionMarker === JpegMarkers.SOS) {
			// Start of image data
			break
		}

		const sectionLength = dataStream.getUint16()
		const sectionId = dataStream.getText(4)
		const nextSectionOffset = sectionStartOffset + sectionLength + 2

		const code = JpegMarkers[sectionMarker]

		if (typeof code === "string") {
			markers[code] = {
				start: sectionStartOffset,
				end: nextSectionOffset,
				id: sectionId,
			}
		}

		dataStream.offset = nextSectionOffset
	}

	return markers
}

export async function getJpegMarkers(file: Blob) {
	return getJpegMarkersFromBytes(new Uint8Array(await file.slice(0, 1024).arrayBuffer()))
}

export function makeSegmentHeader(marker: JpegMarkers, id: string, bodySize: number) {
	const identifierSize = Math.max(
		6, // id (4 bytes) + padding (2 bytes)
		id.length + 1, // id longer than 4 bytes + 1 byte padding
	)
	const segmentHeaderSize =
		2 + // segment marker bytes
		2 + // segment size bytes
		identifierSize
	const header = new ArrayBuffer(segmentHeaderSize)
	const dataStream = new DataStream(header)

	// JPEG is big-endian
	dataStream.littleEndian = false

	// Write JPEG segment header
	dataStream.setUint16(marker)
	dataStream.setUint16(bodySize + segmentHeaderSize - 2) // minus marker size
	dataStream.setText(id)

	return header
}
